import Dialog from '@jetbrains/ring-ui/components/dialog/dialog'
import {Col} from '@jetbrains/ring-ui/components/grid/grid'
import {Content, Header} from '@jetbrains/ring-ui/components/island/island'
import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import {skipToken} from '@reduxjs/toolkit/query'
import classNames from 'classnames'
import ordinal from 'ordinal'
import * as React from 'react'

import {useDistanceToNow} from '../../../../hooks/useTimeSelector'
import {getCompareBuildHref} from '../../../../routes'
import {restApi} from '../../../../services/rest'
import type {BuildState, BuildStatus} from '../../../../types'
import {stringifyId} from '../../../../types'
import {isEventWithSpecialKeyPressed} from '../../../../utils/keys'
import BuildTypeBranch from '../../../common/BranchLabel/BuildTypeBranchLabel.container'
import statusStyles from '../../../common/BuildStatus/BuildStatusLink/BuildStatusLink.css'
import {useTitle} from '../../../common/BuildStatus/BuildStatusLink/BuildStatusLink.utils'
import Icon from '../../../common/Icon/Icon'
import ListItem from '../../../common/ListItem/ListItem'
import RouterLink from '../../../common/RouterLink/RouterLink'
import type {BuildForCompare} from '../CompareBuildsPage.types'

import type {Props} from './CompareBuildDialog.types'

import styles from './CompareBuildDialog.css'

type CompareBuildDialogBuildProps = BuildForCompare & {
  label?: string | null | undefined
}

function CompareBuildDialogBuild({label, ...build}: CompareBuildDialogBuildProps) {
  let number = 'N/A'

  if (build.number != null) {
    number = `#${build.number}`
  } else if (build.state === 'queued' && build.queuePosition != null) {
    number = ordinal(build.queuePosition)
  }

  const getStatusIcon: (arg0: BuildState, arg1: BuildStatus) => string = (buildState, status) => {
    const modifiers = []

    if (buildState === 'queued') {
      modifiers.push(buildState)
    } else {
      modifiers.push(buildState === 'running' ? buildState : 'finished')
      const isGreen = status == null || status === 'SUCCESS'
      modifiers.push(isGreen ? 'green' : 'red')
    }

    return modifiers.join('_')
  }

  const startFromNow = useDistanceToNow(build.startDate)
  const triggeredFromNow = useDistanceToNow(build.triggered.date)
  const title = useTitle({
    statusText: build.statusText,
    isQueued: build.state === 'queued',
    waitReason: null,
    startEstimate: build.startEstimate,
  })

  return (
    <ListItem hideTopBorder>
      <Col xs>
        <div className={styles.buildContent}>
          <div className={styles.firstRow}>
            <div className={styles.numberWithBranch}>
              <span className={styles.number}>{number}</span>
              {build.branchName != null && (
                <BuildTypeBranch
                  buildTypeId={build.buildTypeId}
                  name={build.branchName}
                  className={styles.branch}
                  noLink
                />
              )}
            </div>
            {label != null && label.length > 0 && <div className={styles.label}>{label}</div>}
            {label == null && build.startDate != null && (
              <div className={styles.label}>{startFromNow}</div>
            )}
            {label == null && build.state === 'queued' && build.triggered.date != null && (
              <div className={styles.label}>{`Added to queue ${triggeredFromNow}`}</div>
            )}
          </div>
          <div className={styles.status}>
            {build.state && (
              <>
                <Icon
                  name={getStatusIcon(build.state, build.status)}
                  className={styles.statusIcon}
                />
                <span
                  className={classNames({
                    [statusStyles.success]: build.status === 'SUCCESS',
                    [statusStyles.failure]: build.status === 'FAILURE',
                    [statusStyles.warning]: build.status === 'WARNING',
                  })}
                >
                  {title}
                </span>
              </>
            )}
          </div>
        </div>
      </Col>
    </ListItem>
  )
}

function CompareBuildDialog(props: Props) {
  const {isOpened, closeDialog, compareWithId, isProcessing, endpoint, buildId} = props
  const {data} = restApi.endpoints.getBuildsForCompare.useQuery(
    isOpened && endpoint != null ? {endpoint, sourceId: buildId} : skipToken,
    {selectFromResult: ({data}) => ({data})},
  )
  const ready = data != null
  const buildsRef = React.useRef<HTMLDivElement>(null)
  const focused = React.useRef(false)

  const focusLink = React.useCallback((elementToFocus: Node | null) => {
    if (elementToFocus != null && elementToFocus instanceof HTMLElement) {
      focused.current = true
      elementToFocus.focus()
    }
  }, [])

  const focusFirstLink = React.useCallback(() => {
    if (buildsRef.current) {
      const elementToFocus = buildsRef.current.firstChild
      focusLink(elementToFocus)
    }
  }, [focusLink])

  React.useEffect(() => {
    focusFirstLink()
  }, [focusFirstLink, ready])

  React.useEffect(
    () => () => {
      if (isOpened) {
        closeDialog()
      }
    },
    [closeDialog, compareWithId, isOpened],
  )

  const handleKeyDown: (arg0: React.KeyboardEvent) => void = e => {
    const currentTarget = e.currentTarget

    if (
      e.target !== currentTarget ||
      !buildsRef.current ||
      !(currentTarget instanceof HTMLElement)
    ) {
      return
    }

    let itemToFocus: Node | null | undefined

    switch (e.key) {
      case 'Home':
        itemToFocus = buildsRef.current.firstChild
        break

      case 'End':
        itemToFocus = buildsRef.current.lastChild
        break

      case 'ArrowUp':
        itemToFocus = currentTarget.previousElementSibling
        break

      case 'ArrowDown':
        itemToFocus = currentTarget.nextElementSibling
        break

      default:
        return
    }

    e.stopPropagation()
    e.preventDefault()
    focusLink(itemToFocus)
  }

  const getHref: (arg0: BuildForCompare) => string = build => {
    const {currentTab, invertBuildsInLinks} = props
    const source = invertBuildsInLinks === true ? build.id : buildId
    const withBuildId = invertBuildsInLinks === true ? buildId : build.id
    return `${getCompareBuildHref(source)}?withBuildId=${stringifyId(withBuildId)}${
      currentTab != null ? `&tab=${currentTab}` : ''
    }`
  }

  const onLinkClick: (event: React.MouseEvent) => void = event => {
    const {onSelect} = props

    if (onSelect != null && !isEventWithSpecialKeyPressed(event)) {
      onSelect()
    }
  }

  const renderBuild = (build: BuildForCompare, label?: string | null) => {
    const {isSakuraUI} = props
    const key = `${label ?? ''}-${stringifyId(build.id)}`
    return isSakuraUI ? (
      <RouterLink
        key={key}
        to={getHref(build)}
        className={styles.build}
        onKeyDown={handleKeyDown}
        onClick={onLinkClick}
      >
        <CompareBuildDialogBuild {...build} label={label} />
      </RouterLink>
    ) : (
      <a
        key={key}
        href={getHref(build)}
        className={styles.build}
        onKeyDown={handleKeyDown}
        onClick={onLinkClick}
      >
        <CompareBuildDialogBuild {...build} label={label} />
      </a>
    )
  }

  const renderData: () => React.ReactNode = () => {
    const list: ReadonlyArray<BuildForCompare> = data?.builds ?? []

    if (list.length === 0) {
      return <span className={styles.emptyMessage}>{'No builds to compare with'}</span>
    }

    return (
      <div ref={buildsRef}>
        {data?.lastSuccessful && renderBuild(data.lastSuccessful, 'Last successful')}
        {data?.lastPinned && renderBuild(data.lastPinned, 'Last pinned')}
        {data?.lastFinished && renderBuild(data.lastFinished, 'Last finished')}
        {list.map((build: BuildForCompare) => renderBuild(build))}
      </div>
    )
  }

  const title = 'Select Build to Compare'
  return isOpened ? (
    <Dialog
      label={title}
      show
      onCloseAttempt={isProcessing ? undefined : closeDialog}
      showCloseButton={!isProcessing}
      trapFocus
      autoFocusFirst
      contentClassName={styles.dialog}
    >
      <Header>{title}</Header>
      <Content scrollableWrapperClassName={styles.dialogContent}>
        {ready ? (
          renderData()
        ) : (
          <LoaderInline className={styles.loader}>{'Loading...'}</LoaderInline>
        )}
      </Content>
    </Dialog>
  ) : null
}

export default CompareBuildDialog
